import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import client from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Input } from "../components/ui";
import { LANGS, RTL, APP_FULL } from "../i18n";
import { appLogo } from "../appLogos";
import { Image as ImageIcon, Download, Smartphone, Layers } from "lucide-react";

const APP_KEYS = ["family_tree", "holdings", "currency", "benefits", "master_enabler"];
const SHOT = (k) => `/store-shots/${k}.png`;
const THEMES = [
  { name: "Blue", bg: "linear-gradient(160deg,#0F62FE 0%,#0A2A66 100%)", fg: "#ffffff" },
  { name: "Night", bg: "linear-gradient(160deg,#161616 0%,#0A0A0A 100%)", fg: "#ffffff" },
  { name: "Light", bg: "linear-gradient(160deg,#F4F4F4 0%,#D8E2FF 100%)", fg: "#0A0A0A" },
];

export default function StoreScreenshots() {
  const { lang } = useApp();
  const [listings, setListings] = useState({});
  const [active, setActive] = useState(lang || "ENG");
  const [appKey, setAppKey] = useState("family_tree");
  const [theme, setTheme] = useState(0);
  const [headline, setHeadline] = useState("");
  const [sub, setSub] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const frameRef = useRef(null);

  useEffect(() => { client.get("/store-listings").then((r) => setListings(r.data)).catch(() => {}); }, []);

  // populate headline/sub from listings on lang/app change
  useEffect(() => {
    const c = listings?.[active]?.[appKey];
    if (c) { setHeadline(c.title || ""); setSub(c.short_description || ""); }
  }, [listings, active, appKey]);

  const isRtl = RTL.includes(active);
  const t = THEMES[theme];

  // Build an off-screen frame element for any combination and capture it to a PNG dataURL.
  const renderCombo = async ({ ak, code, themeIdx, hl, sb }) => {
    const th = THEMES[themeIdx];
    const rtl = RTL.includes(code);
    const el = document.createElement("div");
    el.dir = rtl ? "rtl" : "ltr";
    el.style.cssText = `width:360px;height:640px;background:${th.bg};color:${th.fg};position:fixed;left:-10000px;top:0;border-radius:18px;overflow:hidden;padding:24px;box-sizing:border-box;font-family:'IBM Plex Sans',sans-serif`;

    const head = document.createElement("div");
    head.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:8px";
    const logo = document.createElement("img");
    logo.src = "/brand-logo.png"; logo.crossOrigin = "anonymous";
    logo.style.cssText = `height:22px;${th.fg === "#ffffff" ? "filter:brightness(0) invert(1)" : ""}`;
    const wm = document.createElement("span");
    wm.textContent = "FULL OPTION FAMILY OFFICE";
    wm.style.cssText = "font-family:Outfit,sans-serif;font-size:11px;letter-spacing:2px;opacity:.85";
    head.appendChild(logo); head.appendChild(wm);

    const h2 = document.createElement("h2");
    h2.textContent = hl; h2.style.cssText = "font-family:Outfit,sans-serif;font-size:24px;line-height:1.15;margin:6px 0;font-weight:600";
    const p = document.createElement("p");
    p.textContent = sb; p.style.cssText = "font-size:13px;opacity:.9;margin:0 0 14px";

    const appIcon = document.createElement("img");
    appIcon.src = appLogo(ak); appIcon.crossOrigin = "anonymous";
    appIcon.style.cssText = "position:absolute;top:20px;right:20px;width:52px;height:52px;object-fit:contain;background:#fff;border-radius:13px;padding:5px;box-shadow:0 6px 18px rgba(0,0,0,.25)";

    const phone = document.createElement("div");
    phone.style.cssText = "position:absolute;left:50%;bottom:-40px;transform:translateX(-50%);width:230px;height:430px;background:#000;border-radius:30px;padding:8px;box-shadow:0 20px 60px rgba(0,0,0,.35)";
    const screen = document.createElement("div");
    screen.style.cssText = "width:100%;height:100%;border-radius:24px;overflow:hidden;background:#fff";
    const shot = document.createElement("img");
    shot.src = SHOT(ak); shot.crossOrigin = "anonymous";
    shot.style.cssText = "width:100%;height:100%;object-fit:cover;object-position:top";
    screen.appendChild(shot); phone.appendChild(screen);

    el.appendChild(head); el.appendChild(h2); el.appendChild(p); el.appendChild(appIcon); el.appendChild(phone);
    document.body.appendChild(el);
    await Promise.all([logo, shot, appIcon].map((im) => im.complete ? Promise.resolve() : new Promise((r) => { im.onload = r; im.onerror = r; })));
    await new Promise((r) => setTimeout(r, 60));
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: null, logging: false });
    document.body.removeChild(el);
    return canvas.toDataURL("image/png");
  };

  const runBatch = async (combos, zipName) => {
    setBusy(true); setProgress(`0/${combos.length}`);
    try {
      const files = [];
      for (let i = 0; i < combos.length; i++) {
        const { ak, code } = combos[i];
        const c = listings?.[code]?.[ak] || {};
        const dataUrl = await renderCombo({ ak, code, themeIdx: theme, hl: c.title || "", sb: c.short_description || "" });
        files.push({ name: `store_${ak}_${code}.png`, data: dataUrl.split(",")[1] });
        setProgress(`${i + 1}/${combos.length}`);
      }
      const res = await client.post("/store-screenshots/zip", { files }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = zipName; a.click();
      URL.revokeObjectURL(url);
    } finally { setBusy(false); setProgress(""); }
  };

  const batchApps = () => runBatch(APP_KEYS.map((ak) => ({ ak, code: active })), `store_screenshots_${active}_all_apps.zip`);
  const batchLangs = () => runBatch(LANGS.map((l) => ({ ak: appKey, code: l.code })), `store_screenshots_${appKey}_all_langs.zip`);
  const batchAll = () => runBatch(APP_KEYS.flatMap((ak) => LANGS.map((l) => ({ ak, code: l.code }))), `store_screenshots_everything.zip`);

  const exportPng = async (download = true) => {
    if (!frameRef.current) return null;
    setBusy(true);
    try {
      const canvas = await html2canvas(frameRef.current, { scale: 2, useCORS: true, backgroundColor: null, logging: false });
      const dataUrl = canvas.toDataURL("image/png");
      if (download) {
        const a = document.createElement("a");
        a.href = dataUrl; a.download = `store_${appKey}_${active}.png`; a.click();
      }
      return dataUrl;
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 fadeup">
      <div className="flex flex-wrap items-center gap-3">
        <ImageIcon className="text-[var(--primary)]" />
        <h1 className="font-head text-2xl sm:text-3xl">Screenshot Studio</h1>
        <span className="opacity-60 text-sm">Compose branded, device-framed store screenshots per language</span>
        <div className="ms-auto flex flex-wrap gap-2">
          <Button variant="ghost" onClick={batchApps} disabled={busy} data-testid="batch-apps"><Layers size={16} /> All apps ({active})</Button>
          <Button variant="ghost" onClick={batchLangs} disabled={busy} data-testid="batch-langs"><Layers size={16} /> All 12 langs</Button>
          <Button variant="ghost" onClick={batchAll} disabled={busy} data-testid="batch-all"><Layers size={16} /> Everything (60)</Button>
          <Button onClick={() => exportPng(true)} disabled={busy} data-testid="download-screenshot"><Download size={16} /> {busy ? (progress || "Rendering…") : "Download PNG"}</Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        {/* Controls */}
        <div className="space-y-4">
          <div>
            <div className="mb-1 text-xs font-medium opacity-70">Application</div>
            <div className="flex flex-wrap gap-2">
              {APP_KEYS.map((k) => (
                <button key={k} onClick={() => setAppKey(k)} data-testid={`app-${k}`}
                  className={`rounded-md px-2.5 py-1 text-xs lift ${appKey === k ? "bg-[var(--primary)] text-white" : "bg-black/5 dark:bg-white/10"}`}>
                  {k.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium opacity-70">Language</div>
            <div className="flex flex-wrap gap-1.5" data-testid="ss-lang-tabs">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setActive(l.code)} data-testid={`ss-lang-${l.code}`}
                  className={`rounded-full px-2.5 py-1 text-xs lift ${active === l.code ? "bg-[var(--primary)] text-white" : "bg-black/5 dark:bg-white/10"}`}>
                  {l.native}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium opacity-70">Theme</div>
            <div className="flex gap-2">
              {THEMES.map((th, i) => (
                <button key={th.name} onClick={() => setTheme(i)} data-testid={`theme-${i}`}
                  className={`rounded-md px-3 py-1 text-xs lift ${theme === i ? "ring-2 ring-[var(--primary)]" : ""}`}
                  style={{ background: th.bg, color: th.fg }}>{th.name}</button>
              ))}
            </div>
          </div>
          <Input label="Headline" value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="ss-headline" />
          <Input label="Subheadline" value={sub} onChange={(e) => setSub(e.target.value)} data-testid="ss-sub" />
        </div>

        {/* Live preview frame (1080x1920 store ratio scaled down) */}
        <div className="overflow-auto">
          <div ref={frameRef} dir={isRtl ? "rtl" : "ltr"} data-testid="ss-canvas"
            style={{ width: 360, height: 640, background: t.bg, color: t.fg, position: "relative",
                     borderRadius: 18, overflow: "hidden", padding: 24, boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <img src="/brand-logo.png" alt="" crossOrigin="anonymous"
                style={{ height: 22, filter: t.fg === "#ffffff" ? "brightness(0) invert(1)" : "none" }} />
              <span style={{ fontFamily: "Outfit, sans-serif", fontSize: 11, letterSpacing: 2, opacity: 0.85 }}>FULL OPTION FAMILY OFFICE</span>
            </div>
            <h2 style={{ fontFamily: "Outfit, sans-serif", fontSize: 24, lineHeight: 1.15, margin: "6px 0", fontWeight: 600 }}>{headline}</h2>
            <p style={{ fontSize: 13, opacity: 0.9, margin: "0 0 14px" }}>{sub}</p>
            <img src={appLogo(appKey)} alt="" crossOrigin="anonymous" data-testid="ss-app-icon"
              style={{ position: "absolute", top: 20, right: 20, width: 52, height: 52, objectFit: "contain",
                       background: "#fff", borderRadius: 13, padding: 5, boxShadow: "0 6px 18px rgba(0,0,0,0.25)" }} />
            {/* phone frame */}
            <div style={{ position: "absolute", left: "50%", bottom: -40, transform: "translateX(-50%)",
                          width: 230, height: 430, background: "#000", borderRadius: 30, padding: 8,
                          boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
              <div style={{ width: "100%", height: "100%", borderRadius: 24, overflow: "hidden", background: "#fff" }}>
                <img src={SHOT(appKey)} alt="" crossOrigin="anonymous"
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
              </div>
            </div>
          </div>
          <p className="mt-3 text-xs opacity-60">Preview at 360×640. Exported PNG is 2× ({720}×{1280}). Tip: switch language to auto-fill the localized headline.</p>
        </div>
      </div>
    </div>
  );
}
